import {
  Color3,
  Color4,
  FxaaPostProcess,
  DirectionalLight,
  Engine,
  GlowLayer,
  HemisphericLight,
  type Mesh,
  Scene,
  SceneLoader,
  ShadowGenerator,
  type AbstractMesh,
  type Camera,
  Vector3,
  WebGPUEngine,
  type AbstractEngine,
  PBRMaterial,
  StandardMaterial,
  type Material,
  VertexBuffer,
} from "@babylonjs/core";
import "@babylonjs/loaders/glTF";
import { type MapId, mapUrl } from "@rivalwatch/shared";
import { lookOf } from "../maps/index.ts";
import { type MapLook, type Owned, stageOf } from "../maps/types.ts";
import "./ink.ts";
import { toon } from "./materials.ts";

export interface World {
  engine: AbstractEngine;
  scene: Scene;
  shadows: ShadowGenerator;
  mapMeshes: AbstractMesh[];
  glow: GlowLayer;
  /** The loaded map's look (sky, light, ink, payload and track dressing). */
  look: MapLook;
  /** The look's ink colour. */
  ink: Color4;
  /** The stage (Domination sub-map) on show; a map without stages only has stage 0. */
  stage: number;
  attachCamera(cam: Camera): void;
  /** Swaps in another map, with its look and scenery. */
  loadMap(id: MapId): Promise<void>;
  /** Shows one stage of a map built in stages (its parts, scenery, shadows, sky and light). */
  setStage(stage: number): void;
  dispose(): void;
}

/** How the map's batch meshes (VIS_*, see tools/map/mapkit.py) ask to be drawn. */
interface BatchLook {
  look?: string;
  ink?: "crease" | "soft" | "hull" | "far" | "none";
  shadow?: number;
  fog?: number;
  alpha?: number;
  /** Unlit, in its own colour, and picked up by the glow layer (neon). */
  emit?: number;
}

/**
 * Unfolds a mesh so every triangle has its own corners, with that face's normal: flat colour per
 * face. (Mesh.convertToFlatShadedMesh rewrites the index array in place, and a glTF's 16-bit
 * indices wrap past 65,535 corners, scrambling big batches.)
 */
function flatShade(mesh: Mesh) {
  const pos = mesh.getVerticesData(VertexBuffer.PositionKind)!;
  const col = mesh.getVerticesData(VertexBuffer.ColorKind);
  const cs = mesh.getVertexBuffer(VertexBuffer.ColorKind)?.getSize() ?? 4;
  const idx = mesh.getIndices()!;
  const n = idx.length;
  const P = new Float32Array(n * 3);
  const N = new Float32Array(n * 3);
  const C = col ? new Float32Array(n * 4) : undefined;
  const I = new Uint32Array(n);
  for (let t = 0; t < n; t += 3) {
    const a = idx[t]! * 3;
    const b = idx[t + 1]! * 3;
    const c = idx[t + 2]! * 3;
    const ux = pos[b]! - pos[a]!, uy = pos[b + 1]! - pos[a + 1]!, uz = pos[b + 2]! - pos[a + 2]!;
    const vx = pos[c]! - pos[a]!, vy = pos[c + 1]! - pos[a + 1]!, vz = pos[c + 2]! - pos[a + 2]!;
    let nx = uy * vz - uz * vy, ny = uz * vx - ux * vz, nz = ux * vy - uy * vx;
    const l = Math.hypot(nx, ny, nz) || 1;
    nx /= l;
    ny /= l;
    nz /= l;
    for (let k = 0; k < 3; k++) {
      const v = idx[t + k]!;
      const o = (t + k) * 3;
      P[o] = pos[v * 3]!;
      P[o + 1] = pos[v * 3 + 1]!;
      P[o + 2] = pos[v * 3 + 2]!;
      N[o] = nx;
      N[o + 1] = ny;
      N[o + 2] = nz;
      if (C && col) {
        C[(t + k) * 4] = col[v * cs]!;
        C[(t + k) * 4 + 1] = col[v * cs + 1]!;
        C[(t + k) * 4 + 2] = col[v * cs + 2]!;
        C[(t + k) * 4 + 3] = cs === 4 ? col[v * cs + 3]! : 1;
      }
      I[t + k] = t + k;
    }
  }
  mesh.setVerticesData(VertexBuffer.PositionKind, P, false);
  mesh.setVerticesData(VertexBuffer.NormalKind, N, false);
  if (C) mesh.setVerticesData(VertexBuffer.ColorKind, C, false, 4);
  mesh.setIndices(I);
}

/** Engine + scene + map (the Foundry by default), cel-shaded with ink edges. Shared by the menu backdrop and the game. */
export async function createWorld(canvas: HTMLCanvasElement, mapId: MapId = "foundry"): Promise<World> {
  const wantGpu = new URLSearchParams(location.search).get("webgpu") === "1";
  let engine: AbstractEngine;
  if (wantGpu && (await WebGPUEngine.IsSupportedAsync)) {
    const gpu = new WebGPUEngine(canvas, { antialias: true, stencil: true });
    await gpu.initAsync();
    engine = gpu;
  } else {
    engine = new Engine(canvas, true, { stencil: true, preserveDrawingBuffer: false, powerPreference: "high-performance" }, true);
  }
  const scene = new Scene(engine);
  scene.useRightHandedSystem = true;
  scene.fogMode = Scene.FOGMODE_EXP2;
  scene.skipPointerMovePicking = true;
  // We never pick with the pointer; don't let Babylon capture or preventDefault pointer input
  // (that swallows the browser's mouse events the game reads).
  scene.preventDefaultOnPointerDown = false;
  scene.preventDefaultOnPointerUp = false;
  scene.detachControl();

  const hemi = new HemisphericLight("hemi", new Vector3(0.2, 1, 0.1), scene);
  const sun = new DirectionalLight("sun", new Vector3(-0.45, -1, -0.3), scene);
  // Fit the shadow map's depth range to the casters, wherever the sun stands. The frustum spans the
  // whole map, and 4096 keeps texels near 10 cm on Rivals-sized maps.
  sun.autoCalcShadowZBounds = true;
  const shadows = new ShadowGenerator(4096, sun);
  shadows.usePercentageCloserFiltering = true;
  shadows.bias = 0.0015;
  shadows.normalBias = 0.04;

  const gl = new GlowLayer("glow", scene, { mainTextureSamples: 2, blurKernelSize: 24 });

  const applyLook = (l: MapLook) => {
    scene.clearColor = Color4.FromHexString(`${l.sky.horizon ?? l.sky.top}ff`);
    scene.fogColor = Color3.FromHexString(l.fog.color);
    scene.fogDensity = l.fog.density;
    hemi.direction = new Vector3(...l.hemi.dir);
    hemi.intensity = l.hemi.intensity;
    hemi.diffuse = Color3.FromHexString(l.hemi.sky);
    hemi.groundColor = Color3.FromHexString(l.hemi.ground);
    sun.direction = new Vector3(...l.sun.dir).normalize();
    sun.position = sun.direction.scale(-200);
    sun.intensity = l.sun.intensity;
    sun.diffuse = Color3.FromHexString(l.sun.color);
    shadows.darkness = l.shadowDarkness;
    gl.intensity = l.glow;
  };

  const mapMeshes: AbstractMesh[] = [];
  let loaded: AbstractMesh[] = [];
  let dressed: Owned[] = [];
  let batchMaterials: Material[] = [];
  /** Everything that belongs to one stage: only the stage on show is drawn and casts shadows. */
  let staged: { node: AbstractMesh; stage: number; caster: boolean }[] = [];
  /** Faint-inked backdrops, whose ink fades into the stage's own fog. */
  let farInked: Mesh[] = [];
  const world: World = {
    engine,
    scene,
    shadows,
    mapMeshes,
    glow: gl,
    look: lookOf(mapId),
    ink: new Color4(0, 0, 0, 1),
    stage: 0,
    attachCamera: () => undefined,
    loadMap: async () => undefined,
    setStage: () => undefined,
    dispose: () => undefined,
  };

  const faintInk = (ink: Color4, fog: string) => Color4.FromColor3(Color3.Lerp(new Color3(ink.r, ink.g, ink.b), Color3.FromHexString(fog), 0.55), 1);

  world.setStage = (k: number) => {
    world.stage = k;
    for (const s of staged) {
      const on = s.stage === k;
      s.node.setEnabled(on);
      if (!s.caster) continue;
      if (on) shadows.addShadowCaster(s.node);
      else shadows.removeShadowCaster(s.node);
    }
    const l = { ...world.look, ...world.look.stages?.[k] };
    applyLook(l);
    const faint = faintInk(world.ink, l.fog.color);
    for (const m of farInked) m.edgesColor = faint;
  };

  world.loadMap = async (id: MapId) => {
    const look = lookOf(id);
    const res = await SceneLoader.ImportMeshAsync("", "", mapUrl(id), scene, undefined, ".glb");
    // Out with the old map only once the new one is in, so there's never an empty frame.
    for (const m of loaded) {
      shadows.removeShadowCaster(m);
      m.dispose(false, false);
    }
    for (const d of dressed) d.dispose();
    for (const m of batchMaterials) m.dispose();
    batchMaterials = [];
    loaded = res.meshes;
    mapMeshes.length = 0;
    staged = [];
    farInked = [];
    applyLook(look);
    world.look = look;
    const ink = Color4.FromHexString(`${look.ink.color}ff`);
    world.ink = ink;
    const inkLine = new Color3(ink.r, ink.g, ink.b);
    const faint = faintInk(ink, look.fog.color);
    /** Casts shadows: at once, or while its stage is on show. */
    const caster = (m: AbstractMesh) => {
      const k = stageOf(m);
      if (k === undefined) shadows.addShadowCaster(m);
      else staged.push({ node: m, stage: k, caster: true });
    };
    const batchMats = new Map<string, StandardMaterial>();
    const panels: Mesh[] = [];
    let extent = 0;
    for (const m of res.meshes) {
      const name = m.name;
      if (name.startsWith("COL_") || name.startsWith("SPAWNROOM_") || name.startsWith("GATE_")) {
        m.setEnabled(false);
        continue;
      }
      if (!m.getTotalVertices()) continue;
      m.isPickable = false;
      if (name.startsWith("MURAL_") || name.startsWith("SIGN_")) {
        panels.push(m as Mesh);
        const k = stageOf(m);
        if (k !== undefined) staged.push({ node: m, stage: k, caster: false });
        continue;
      }
      const src = m.material;
      const bl = ((src?.metadata as { gltf?: { extras?: BatchLook } } | null)?.gltf?.extras ?? {}) as BatchLook;
      const mesh = m as Mesh;
      if (bl.look) {
        // A batch: its parts carry their colours as vertex colours.
        let mat = batchMats.get(bl.look);
        if (!mat) {
          // Flat-shaded Lambert, summed over the sky (hemi) and the sun: every face one flat
          // colour, lit or in shade. (CellMaterial keeps only its last light's term.)
          mat = new StandardMaterial(`look-${bl.look}`, scene);
          mat.diffuseColor = Color3.White();
          mat.specularColor = Color3.Black();
          mat.maxSimultaneousLights = 2;
          if (bl.look === "sky" || bl.emit) {
            mat.disableLighting = true;
            mat.emissiveColor = Color3.White();
          }
          if (bl.alpha !== undefined && bl.alpha < 1) {
            mat.alpha = bl.alpha;
            mat.backFaceCulling = false;
          }
          batchMats.set(bl.look, mat);
          batchMaterials.push(mat);
        }
        m.material = mat;
        src?.dispose();
        // Batches ship without normals (parts share corners, see mapkit.py export): rebuild them,
        // flat per face, or smooth over rounded foliage so its silhouette outline stays whole.
        if (!mesh.isVerticesDataPresent(VertexBuffer.NormalKind)) {
          if (bl.look === "foliage") mesh.createNormals(false);
          else flatShade(mesh);
        }
        if (bl.fog === 0) m.applyFog = false;
        if (bl.shadow) caster(m);
        else if (stageOf(m) !== undefined) staged.push({ node: m, stage: stageOf(m)!, caster: false });
        m.receiveShadows = true;
        const ink4 = bl.ink ?? "crease";
        if (ink4 === "crease" || ink4 === "far") {
          mesh.enableEdgesRendering(0.95);
          mesh.edgesWidth = ink4 === "far" ? look.ink.width * 0.6 : look.ink.width;
          mesh.edgesColor = ink4 === "far" ? faint : ink;
          if (ink4 === "far") farInked.push(mesh);
        } else if (ink4 === "soft" || ink4 === "hull") {
          if (ink4 === "soft") {
            mesh.enableEdgesRendering(0.6);
            mesh.edgesWidth = look.ink.width;
            mesh.edgesColor = ink;
          }
          mesh.renderOutline = true;
          mesh.outlineColor = inkLine;
          mesh.outlineWidth = 0.035;
        }
        if (bl.look === "sky" || bl.look === "backdrop") {
          m.freezeWorldMatrix();
          continue;
        }
      } else {
        let hex = "#b8b2a6";
        if (src instanceof PBRMaterial) hex = src.albedoColor.toGammaSpace().scale(0.86).toHexString();
        else if (src instanceof StandardMaterial) hex = src.diffuseColor.toHexString();
        m.material = toon(scene, hex, { high: false });
        src?.dispose();
        m.receiveShadows = true;
        caster(m);
        mesh.enableEdgesRendering(0.95);
        mesh.edgesWidth = look.ink.width;
        mesh.edgesColor = ink;
      }
      m.computeWorldMatrix(true);
      const b = m.getBoundingInfo().boundingBox;
      extent = Math.max(extent, Math.abs(b.minimumWorld.x), Math.abs(b.maximumWorld.x), Math.abs(b.minimumWorld.z), Math.abs(b.maximumWorld.z));
      m.freezeWorldMatrix();
      mapMeshes.push(m);
    }
    // Ink lines lie exactly on the faces they outline; a hair of depth bias keeps them from
    // flickering into dashes against those faces (the line shader is shared scene-wide).
    const inked = mapMeshes.find((mm) => mm.edgesRenderer)?.edgesRenderer;
    if (inked?.lineShader) {
      inked.lineShader.zOffset = -0.5;
      inked.lineShader.zOffsetUnits = -6;
    }
    dressed = look.dress?.({ engine, scene, shadows, glow: gl, ink, extent, panels }) ?? [];
    const casters = shadows.getShadowMap()?.renderList ?? [];
    for (const d of dressed) {
      if (!("isPickable" in d)) continue;
      const node = d as unknown as AbstractMesh;
      node.isPickable = false;
      const k = stageOf(node);
      if (k === undefined) continue;
      // A stage's scenery casts shadows only while that stage is on show.
      const cast = casters.includes(node);
      if (cast) shadows.removeShadowCaster(node);
      staged.push({ node, stage: k, caster: cast });
    }
    world.setStage(0);
  };
  await world.loadMap(mapId);

  // Note: DefaultRenderingPipeline drops EdgesRenderer output, so only FXAA is used here.
  let fxaa: FxaaPostProcess | undefined;
  world.attachCamera = (cam: Camera) => {
    fxaa?.dispose();
    fxaa = new FxaaPostProcess("fxaa", 1.0, cam);
  };

  const onResize = () => engine.resize();
  window.addEventListener("resize", onResize);
  world.dispose = () => {
    window.removeEventListener("resize", onResize);
    fxaa?.dispose();
    scene.dispose();
    engine.dispose();
  };
  return world;
}
